import "./TimeSeriesComponent.scss";
import desktopTemplate from "./TimeSeriesDesktopComponent.html";
import mobileTemplate from "./TimeSeriesMobileComponent.html";
import {IComponentOptions} from "angular";
import {CodeAndExchange, securityEquality} from "../../../instruments/types/CodeAndExchange";
import {Observable, ReplaySubject} from "rxjs";
import {TimeSeriesPeriod} from "../types/TimeSeriesPeriod";
import {TimeSeriesService} from "../service/TimeSeriesService";
import {PriceStrategyService} from "../../../components/inputs/price/service/PriceStrategyService";
import {BuildViewModels, TimeSeriesViewModels} from "../models/TimeSeriesViewModel";

export const TimeSeriesDesktop = "tfSecurityTimeSeriesDesktop";
export function timeSeriesDesktop(): IComponentOptions {
    return {
        template: desktopTemplate,
        bindings: {
            security: "<",
            period: "<",
            adjusted: "<"
        },
        controllerAs: "ctrl",
        controller: TimeSeriesComponentController
    };
}

export const TimeSeriesMobile = "tfSecurityTimeSeriesMobile";

export function timeSeriesMobile(): IComponentOptions {
    return {
        template: mobileTemplate,
        bindings: {
            security: "<",
            period: "<",
            adjusted: "<"
        },
        controllerAs: "ctrl",
        controller: TimeSeriesComponentController
    };
}

export class TimeSeriesComponentController {

    /** @input */
    public set security(security: CodeAndExchange | null) {
        if (security != null) {
            this.security$.next(security);
        }
    }

    private readonly security$ = new ReplaySubject<CodeAndExchange>(1);

    /** @input */
    public set period(period: TimeSeriesPeriod | null) {
        if (period != null) {
            this.period$.next(period);
        }
    }

    private readonly period$ = new ReplaySubject<TimeSeriesPeriod>(1);

    /** @input */
    public set adjusted(adjusted: boolean | null) {
        if (adjusted == null) {
            this.adjusted$.next(true);
        } else {
            this.adjusted$.next(adjusted);
        }
    }

    private readonly adjusted$ = new ReplaySubject<boolean>(1);

    public readonly data$: Observable<TimeSeriesViewModels>;

    /** @ngInject */
    public constructor(private readonly timeSeriesService: TimeSeriesService,
                       private readonly priceStrategyService: PriceStrategyService) {
        this.data$ = this.buildData();
    }

    private buildData(): Observable<TimeSeriesViewModels> {
        const adjusted$ = this.adjusted$.startWith(true).distinctUntilChanged();
        const security$ = this.security$.distinctUntilChanged(securityEquality());
        const period$ = this.period$.distinctUntilChanged();
        const source$ = Observable.combineLatest(security$, period$, adjusted$);
        return source$.switchMap(([security, period, adjusted]) => this.retrieveData(security, period, adjusted))
                      .publishReplay(1)
                      .refCount();
    }

    private retrieveData(security: CodeAndExchange,
                         period: TimeSeriesPeriod,
                         adjusted: boolean): Observable<TimeSeriesViewModels> {
        const result = this.timeSeriesService.load(security, period, adjusted);
        const data$ = Observable.fromPromise(result);
        const strategy$ = Observable.of(security)
                                    .map((s) => s.code)
                                    .map((code) => this.priceStrategyService.forCode(code));
        return Observable.combineLatest(data$, strategy$)
                         .map(([data, strategy]) => BuildViewModels(period, data, strategy))
                         .catch(() => Observable.empty());
    }
}
